import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { CreditCard, Bell, Plus, Trash2, Star } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

export function CustomerSettings({ me, onSaved }) {
  const { t } = useTranslation();
  const [phone, setPhone] = useState(me?.phone || "");
  const [cur, setCur] = useState("");
  const [nw, setNw] = useState("");
  const [conf, setConf] = useState("");
  const [s1, setS1] = useState(false);
  const [s2, setS2] = useState(false);
  const [cards, setCards] = useState(null);
  const [cardBusy, setCardBusy] = useState("");
  const [prefs, setPrefs] = useState(null);

  const loadCards = () => api.get("/my/payment-methods").then((r) => setCards(r.data.payment_methods)).catch(() => setCards([]));

  useEffect(() => {
    loadCards();
    api.get("/my/notification-prefs").then((r) => setPrefs(r.data)).catch(() => {});
    const params = new URLSearchParams(window.location.search);
    if (params.get("card_saved")) {
      toast.success(t("customerSettings.cardSaved"));
      window.history.replaceState({}, "", "/account");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addCard = async () => {
    setCardBusy("add");
    try {
      const r = await api.post("/my/payment-methods/setup-session", { origin_url: window.location.origin });
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setCardBusy("");
    }
  };

  const removeCard = async (id) => {
    setCardBusy(id);
    try {
      await api.delete(`/my/payment-methods/${id}`);
      toast.success(t("customerSettings.cardRemoved"));
      loadCards();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setCardBusy("");
    }
  };

  const makeDefault = async (id) => {
    setCardBusy(id);
    try {
      await api.post(`/my/payment-methods/${id}/default`);
      toast.success(t("customerSettings.defaultUpdated"));
      loadCards();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setCardBusy("");
    }
  };

  const togglePref = async (key) => {
    const next = { ...prefs, [key]: !prefs[key] };
    setPrefs(next);
    try {
      await api.put("/my/notification-prefs", next);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const savePhone = async () => {
    setS1(true);
    try {
      await api.put("/profile/me", { phone });
      toast.success(t("customerSettings.detailsUpdated"));
      onSaved?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setS1(false);
    }
  };

  const changePw = async () => {
    if (nw !== conf) { toast.error(t("customerSettings.passwordsNoMatch")); return; }
    setS2(true);
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: nw });
      toast.success(t("customerSettings.passwordUpdated"));
      setCur(""); setNw(""); setConf("");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setS2(false);
    }
  };

  return (
    <div className="max-w-2xl space-y-6">
      <div className="bg-white p-8 rounded-2xl border border-slate-200">
        <h3 className="font-heading text-lg font-semibold mb-5">{t("customerSettings.account")}</h3>
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium text-slate-700">{t("customerSettings.name")}</label>
            <input value={me?.name || ""} disabled data-testid="settings-name" className="belo-input w-full mt-1.5 bg-slate-50 text-slate-500" />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700">{t("customerSettings.email")}</label>
            <input value={me?.email || ""} disabled data-testid="settings-email" className="belo-input w-full mt-1.5 bg-slate-50 text-slate-500" />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700">{t("customerSettings.phone")}</label>
            <input value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="settings-phone" className="belo-input w-full mt-1.5" placeholder="(555) 555-5555" />
          </div>
          <button onClick={savePhone} disabled={s1} data-testid="settings-save-phone" className="rounded-full bg-blue-600 text-white px-6 py-2.5 font-semibold text-sm hover:bg-blue-700 disabled:opacity-50">{t("customerSettings.save")}</button>
        </div>
      </div>

      <div className="bg-white p-8 rounded-2xl border border-slate-200" data-testid="payment-methods-card">
        <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
          <h3 className="font-heading text-lg font-semibold flex items-center gap-2"><CreditCard size={18} className="text-blue-600" /> {t("customerSettings.paymentMethods")}</h3>
          <button onClick={addCard} disabled={cardBusy === "add"} data-testid="add-card-btn" className="flex items-center gap-1.5 rounded-full bg-blue-600 text-white px-5 py-2 font-semibold text-sm hover:bg-blue-700 disabled:opacity-50">
            <Plus size={15} /> {cardBusy === "add" ? t("customerSettings.redirecting") : t("customerSettings.addCard")}
          </button>
        </div>
        <p className="text-slate-400 text-xs mb-4">{t("customerSettings.cardsNote")}</p>
        {cards === null ? (
          <p className="text-slate-400 text-sm">…</p>
        ) : cards.length === 0 ? (
          <p data-testid="no-cards" className="text-slate-500 text-sm bg-slate-50 rounded-xl px-4 py-3">{t("customerSettings.noCards")}</p>
        ) : (
          <div className="space-y-3">
            {cards.map((c) => (
              <div key={c.id} data-testid={`card-row-${c.id}`} className="flex items-center justify-between gap-3 border border-slate-200 rounded-xl px-4 py-3">
                <div className="flex items-center gap-3">
                  <span className="grid place-items-center w-9 h-9 rounded-lg bg-slate-100 text-slate-600"><CreditCard size={16} /></span>
                  <div>
                    <p className="text-sm font-semibold text-slate-800 capitalize">{c.brand} •••• {c.last4}
                      {c.is_default && <span className="ml-2 text-[10px] font-bold bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full uppercase">{t("customerSettings.default")}</span>}
                    </p>
                    <p className="text-slate-400 text-xs">{t("customerSettings.expires")} {String(c.exp_month).padStart(2, "0")}/{c.exp_year}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {!c.is_default && (
                    <button onClick={() => makeDefault(c.id)} disabled={cardBusy === c.id} data-testid={`card-default-${c.id}`} title={t("customerSettings.makeDefault")}
                      className="text-slate-400 hover:text-amber-500 disabled:opacity-40"><Star size={16} /></button>
                  )}
                  <button onClick={() => removeCard(c.id)} disabled={cardBusy === c.id} data-testid={`card-remove-${c.id}`} title={t("customerSettings.remove")}
                    className="text-slate-400 hover:text-red-500 disabled:opacity-40"><Trash2 size={16} /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="bg-white p-8 rounded-2xl border border-slate-200" data-testid="notification-prefs-card">
        <h3 className="font-heading text-lg font-semibold mb-2 flex items-center gap-2"><Bell size={18} className="text-blue-600" /> {t("customerSettings.notifications")}</h3>
        <p className="text-slate-400 text-xs mb-5">{t("customerSettings.notificationsNote")}</p>
        {prefs && (
          <div className="space-y-4">
            {[["email", t("customerSettings.emailNotifs")], ["sms", t("customerSettings.smsNotifs")]].map(([key, label]) => (
              <label key={key} data-testid={`pref-${key}`} className="flex items-center justify-between cursor-pointer">
                <span className="text-sm font-medium text-slate-700">{label}</span>
                <button type="button" onClick={() => togglePref(key)} data-testid={`pref-toggle-${key}`}
                  className={`relative w-11 h-6 rounded-full transition-colors ${prefs[key] ? "bg-blue-600" : "bg-slate-300"}`}>
                  <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-all ${prefs[key] ? "left-[22px]" : "left-0.5"}`} />
                </button>
              </label>
            ))}
          </div>
        )}
      </div>

      <div className="bg-white p-8 rounded-2xl border border-slate-200">
        <h3 className="font-heading text-lg font-semibold mb-5">{t("customerSettings.changePassword")}</h3>
        <div className="space-y-4">
          <input type="password" value={cur} onChange={(e) => setCur(e.target.value)} data-testid="settings-current-pw" className="belo-input w-full" placeholder={t("customerSettings.currentPassword")} />
          <input type="password" value={nw} onChange={(e) => setNw(e.target.value)} data-testid="settings-new-pw" className="belo-input w-full" placeholder={t("customerSettings.newPassword")} />
          <input type="password" value={conf} onChange={(e) => setConf(e.target.value)} data-testid="settings-confirm-pw" className="belo-input w-full" placeholder={t("customerSettings.confirmNewPassword")} />
          <button onClick={changePw} disabled={s2} data-testid="settings-change-pw" className="rounded-full bg-blue-600 text-white px-6 py-2.5 font-semibold text-sm hover:bg-blue-700 disabled:opacity-50">{t("customerSettings.updatePassword")}</button>
        </div>
      </div>
    </div>
  );
}
